import { Inject, ChangeDetectionStrategy, Component, DestroyRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    DateUtilities,
    FormatDurationPipe,
    WeeklyWorkTimeInstanceToken,
    WeeklyWorkTimeInterface,
    WorkdayModel,
    StatusBar,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getStatusBarDetails } from '../weekly-work-time-status-bar';

@Component({
    selector: 'pace-weekly-work-time',
    templateUrl: './weekly-work-time.component.html',
    styleUrls: ['./weekly-work-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class WeeklyWorkTimeComponent implements OnChanges {
    @Input()
    public isWorkStartedForToday: boolean;

    public maxWorkingHours: number;
    public totalDurationOfTheWeekInMinutes = 0;
    public statusBarText = '';
    public statusBarType = 'default';

    public weeklyWorkTimeList$: Observable<WorkdayModel[]>;
    public constructor(
        private readonly translate: TranslateService,
        @Inject(WeeklyWorkTimeInstanceToken) private readonly weeklyWorkTimeService: WeeklyWorkTimeInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly destroyRef: DestroyRef,
        private readonly formatDuration: FormatDurationPipe,
    ) {
        this.maxWorkingHours = this.configService.getMaxWeeklyWorkingHours();
        this.weeklyWorkTimeList$ = this.weeklyWorkTimeService.weeklyWorkTimeValue$.pipe(
            tap(() => {
                this.customizeStatusBar();
                this.totalDurationOfTheWeekInMinutes = moment
                    .duration(this.weeklyWorkTimeService.getTotalWeekDuration(), 'hours')
                    .asMinutes();
            }),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj.isWorkStartedForToday) {
            this.isWorkStartedForToday = <boolean>changesObj.isWorkStartedForToday.currentValue;
            this.customizeStatusBar();
        }
    }

    public formatDate(date: moment.Moment): string {
        if (date.isSame(moment(), 'days')) {
            return <string>this.translate.instant('WEEKLY_WORK_HOUR_TODAY_LABEL');
        }
        return DateUtilities.formatDate(date);
    }

    public isWeeklyWorkHoursExceeded(): boolean {
        const maxWeeklyWorkingHours = this.configService.getMaxWeeklyWorkingHours();
        const weeklyWorkHours = this.weeklyWorkTimeService.getTotalWeekDuration();

        return weeklyWorkHours >= maxWeeklyWorkingHours;
    }
    /**
     * This function is to set weekly work time HTML table dependency.
     * Check, if Work duration Exceed NINE hours but less than TEN hours - Status bar color should be Light orange and update table progress bar text.
     * Check if Work duration Exceed TEN Hours - Status bar color should be  orange (dark) and update status bar text.
     */
    private customizeStatusBar(): void {
        const workStatus = this.getStatusBarDetails(this.isWorkStartedForToday);

        this.statusBarType = workStatus?.type ?? '';
        this.statusBarText = workStatus?.text ?? '';
    }

    public getStatusBarDetails(isWorkStartedForToday: boolean): StatusBar | undefined {
        return getStatusBarDetails(
            moment.duration(this.weeklyWorkTimeService.getCurrentDayTotalDuration(), 'hours').asMinutes(),
            isWorkStartedForToday,
            this.translate,
            this.formatDuration,
        );
    }
}
